import type { ReactNode } from "react";
import { X } from "lucide-react";
import { CSS_CLASSES } from "../constants/app";

type PatientDetailModalSize = "sm" | "md" | "lg";

const PANEL_SIZE_CLASS: Record<PatientDetailModalSize, string> = {
  sm: CSS_CLASSES.MODAL.PANEL_SM,
  md: CSS_CLASSES.MODAL.PANEL_MD,
  lg: CSS_CLASSES.MODAL.PANEL_LG,
};

export interface PatientDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: PatientDetailModalSize;
  closeDisabled?: boolean;
  overlayClassName?: string;
  panelClassName?: string;
  bodyClassName?: string;
}

/** Centered / bottom-sheet modal shell for patient detail flows. */
export default function PatientDetailModal({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = "md",
  closeDisabled = false,
  overlayClassName,
  panelClassName,
  bodyClassName,
}: PatientDetailModalProps) {
  if (!isOpen) return null;

  return (
    <div
      className={`${CSS_CLASSES.MODAL.OVERLAY}${overlayClassName ? ` ${overlayClassName}` : ""}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="patient-detail-modal-title"
    >
      <div
        className={`${CSS_CLASSES.MODAL.PANEL} ${PANEL_SIZE_CLASS[size]}${panelClassName ? ` ${panelClassName}` : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={CSS_CLASSES.MODAL.HEADER}>
          <div className="min-w-0 pr-3">
            <h2 id="patient-detail-modal-title" className={CSS_CLASSES.MODAL.TITLE}>
              {title}
            </h2>
            {subtitle ? (
              <p className={CSS_CLASSES.MODAL.SUBTITLE}>{subtitle}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={closeDisabled}
            className={`${CSS_CLASSES.MODAL.CLOSE_BTN} disabled:opacity-50 disabled:cursor-not-allowed`}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div
          className={`${CSS_CLASSES.MODAL.BODY}${bodyClassName ? ` ${bodyClassName}` : ""}`}
        >
          {children}
        </div>
        {footer ? <div className={CSS_CLASSES.MODAL.FOOTER}>{footer}</div> : null}
      </div>
    </div>
  );
}
